import { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useIsUserSignedIn } from "../../core_stores/useAuthStore";
import { useIsCompactView } from "../../core_stores/useIsCompactViewStore";
import { useProjectsListActions } from "../../core_stores/useProjectsListStore";
import { useSubscriptionActions } from "../../core_stores/useSubscriptionStore";
import { usePromptInputActions } from "../../view_stores/usePromptInputStore";
import {
  SideMenuBehavior,
  useArePersonalSettingsOpen,
  useIsSideMenuOpen,
  useSideMenuActions,
  useSideMenuBehavior,
} from "../../view_stores/useSideMenuStore";
import PersonalSettings from "../personal_settings/PersonalSettings";
import SideMenuBottomNav from "./components/SideMenuBottomNav";
import SideMenuHeader from "./components/SideMenuHeader";
import SideMenuProjects from "./components/SideMenuProjects";
import SideMenuSearchField from "./components/SideMenuSearchField";
import SideMenuStartNewProjectBtn from "./components/SideMenuStartNewProjectBtn";

export default function SideMenu() {
  const sideMenuBehavior = useSideMenuBehavior();
  const isSideMenuOpen = useIsSideMenuOpen();
  const arePersonalSettingsOpen = useArePersonalSettingsOpen();
  const isCompactView = useIsCompactView();
  const isUserSignedIn = useIsUserSignedIn();
  const navigate = useNavigate();
  const location = useLocation();
  const menuRef = useRef<HTMLDivElement>(null);

  const {
    close: closeSideMenu,
    setSelectedProjectId,
    switchBehavior,
    allowClose,
  } = useSideMenuActions();
  const { loadProjects, clear: clearProjectsListStore } =
    useProjectsListActions();
  const { clear: clearPromptInput } = usePromptInputActions();
  const { reloadSubscriptionsInfo } = useSubscriptionActions();

  const isProjectPage = location.pathname.startsWith("/project/");

  // Handle scroll lock in compact mode
  useEffect(() => {
    if (isCompactView && (isSideMenuOpen || arePersonalSettingsOpen)) {
      document.body.style.overflow = "hidden";
      document.body.style.height = "100vh";
    } else {
      document.body.style.overflow = "";
      document.body.style.height = "";
    }

    return () => {
      document.body.style.overflow = "";
      document.body.style.height = "";
    };
  }, [isCompactView, isSideMenuOpen, arePersonalSettingsOpen]);

  useEffect(() => {
    if (isUserSignedIn) {
      loadProjects();
      reloadSubscriptionsInfo();
    } else {
      clearProjectsListStore();
      setSelectedProjectId(null);
    }
  }, [isUserSignedIn]);

  // Refresh subscription data when side menu opens
  useEffect(() => {
    if (isUserSignedIn && isSideMenuOpen) {
      reloadSubscriptionsInfo();
    }
  }, [isSideMenuOpen, isUserSignedIn]);

  // Handle mouse leaving the viewport to close side menu in AUTO mode
  useEffect(() => {
    const handleMouseLeave = (event: MouseEvent) => {
      if (
        event.clientX < 0 &&
        sideMenuBehavior === SideMenuBehavior.AUTO &&
        isSideMenuOpen &&
        !arePersonalSettingsOpen
      ) {
        closeSideMenu();
      }
    };

    document.addEventListener("mouseleave", handleMouseLeave);

    return () => {
      document.removeEventListener("mouseleave", handleMouseLeave);
    };
  }, [
    sideMenuBehavior,
    isSideMenuOpen,
    arePersonalSettingsOpen,
    closeSideMenu,
  ]);

  const handleLogoClick = () => {
    if (isProjectPage) {
      window.dispatchEvent(new Event("darvin-block-reopen-menu"));
      closeSideMenu();
      clearPromptInput();
      navigate("/");
    }
  };

  const handleCollapseClick = () => {
    if (sideMenuBehavior === SideMenuBehavior.PINNED) {
      switchBehavior();
    }
    window.dispatchEvent(new Event("darvin-block-reopen-menu"));
    closeSideMenu();
  };

  const shouldBeShown =
    isUserSignedIn &&
    (sideMenuBehavior === SideMenuBehavior.PINNED || isSideMenuOpen);

  return (
    <>
      <SideMenuBackdrop />

      <aside
        ref={menuRef}
        onMouseEnter={allowClose}
        className={`
          ${isCompactView ? "w-full" : "w-80"}
          ${shouldBeShown ? "translate-x-0" : "-translate-x-[110%]"}
          h-full flex flex-col
          fixed inset-y-0 left-0
          bg-dark-surface-primary
          border-r border-dark-border
          transform transition-transform duration-300 ease-in-out
          z-40
        `}
      >
        {/* Header: Logo & Collapse */}
        <SideMenuHeader
          onCollapse={handleCollapseClick}
          onLogoClick={handleLogoClick}
        />

        {/* Middle: Scrollable content */}
        <div className="flex-1 min-h-0 flex flex-col px-4 gap-4 overflow-hidden">
          <SideMenuStartNewProjectBtn />
          <SideMenuSearchField />
          <SideMenuProjects />
        </div>

        {/* Bottom: Navigation (anchored) */}
        <SideMenuBottomNav />
      </aside>

      <PersonalSettings />
    </>
  );
}

function SideMenuBackdrop() {
  const { close: closeSideMenu, allowClose } = useSideMenuActions();
  const sideMenuBehavior = useSideMenuBehavior();
  const isSideMenuOpen = useIsSideMenuOpen();
  const arePersonalSettingsOpen = useArePersonalSettingsOpen();

  const closeByOutsideMove = () => {
    if (
      sideMenuBehavior === SideMenuBehavior.AUTO &&
      !arePersonalSettingsOpen
    ) {
      closeSideMenu();
    }
  };

  const shouldShowBackdrop =
    (sideMenuBehavior === SideMenuBehavior.AUTO && isSideMenuOpen) ||
    (sideMenuBehavior === SideMenuBehavior.PINNED && arePersonalSettingsOpen);

  return (
    <div
      className={`fixed inset-0 w-full h-full z-30 transition-all duration-300 ease-in-out ${
        shouldShowBackdrop
          ? "opacity-100 pointer-events-auto"
          : "opacity-0 pointer-events-none"
        } ${arePersonalSettingsOpen ? "bg-black/25 backdrop-blur-sm" : ""}`}
      onMouseEnter={closeByOutsideMove}
      onClick={() => {
        allowClose();
        closeSideMenu();
      }}
    />
  );
}
